import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export const Skeleton = ({ className }: { className?: string }) => (
  <div aria-hidden className={cn('animate-pulse rounded-lg bg-muted', className)} />
);

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return <Loader2 role="status" aria-label={label} className={cn('size-5 animate-spin text-primary', className)} />;
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="grid min-h-[50vh] place-items-center" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-muted-foreground"><Spinner /> {label}</div>
    </div>
  );
}

export function EmptyState({
  icon: Icon = Inbox, title, description, action, className,
}: { icon?: LucideIcon; title: string; description?: string; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('mx-auto flex max-w-md flex-col items-center px-4 py-14 text-center', className)}>
      <div className="mb-4 grid size-16 place-items-center rounded-2xl bg-primary-soft text-primary"><Icon className="size-8" /></div>
      <h3 className="text-lg font-bold">{title}</h3>
      {description && <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong', error, onRetry, className,
}: { title?: string; error?: Error; onRetry?: () => void; className?: string }) {
  return (
    <div role="alert" className={cn('mx-auto flex max-w-md flex-col items-center px-4 py-14 text-center', className)}>
      <div className="mb-4 grid size-16 place-items-center rounded-2xl bg-danger-soft text-danger"><AlertTriangle className="size-8" /></div>
      <h3 className="text-lg font-bold">{title}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground">{error?.message ?? 'We could not load this. Please try again.'}</p>
      {onRetry && <Button variant="outline" className="mt-5" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** Renders loading / error / empty / content from an AsyncState-like shape, so pages stay declarative. */
export function AsyncView<T>({
  state, loading, empty, isEmpty, children,
}: {
  state: { data: T | undefined; error?: Error; loading: boolean; reload: () => void };
  loading: ReactNode;
  empty?: ReactNode;
  isEmpty?: (d: T) => boolean;
  children: (d: T) => ReactNode;
}) {
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.data === undefined) return <>{loading}</>;
  if (empty && isEmpty?.(state.data)) return <>{empty}</>;
  return <>{children(state.data)}</>;
}
